import type {
    GetAllDtcsLine,
    GetFreezeFramesDocumentRequest,
    GetFreezeFramesErrorResponse,
    GetFreezeFramesFrameResponse,
    GetFreezeFramesParameterResponse,
    GetFreezeFramesReadoutResponse,
    GetFreezeFramesResponse,
    GetFreezeFramesValueResponse,
    GetVehicleByCloudConnectorIdLine,
    RecentConnectionRecord,
    Ssm5BatteryVoltage,
    Ssm5CommStatus,
    Ssm5DtcRecord,
    Ssm5EcuRecord,
} from '@packages/clients';

import type {
    Ssm5ConnectErrorCode,
    Ssm5ConnectPhase,
    Ssm5ConnectResult,
    Ssm5DiagramModule,
    Ssm5Dtc,
    Ssm5DtcDescription,
    Ssm5DtcModule,
    Ssm5DtcsResult,
    Ssm5FreezeFrame,
    Ssm5FreezeFrameParameter,
    Ssm5FreezeFrameReadout,
    Ssm5FreezeFrames,
    Ssm5FreezeFramesDocumentFormat,
    Ssm5FreezeFramesResult,
    Ssm5FreezeFrameValue,
    Ssm5ModuleStatus,
    Ssm5RecentConnection,
    Ssm5TripTime,
} from '../types';

/** An in-progress step of the lookup and the connect phase it stands for. */
type PhaseStep = {
    /** The progress the endpoint reports from this step on. */
    minProgress: number;
    /** The connect phase of the step. */
    phase: Ssm5ConnectPhase;
};

/** The streamed steps from the furthest along to the first, so the first match is the current one. */
const PHASE_STEPS: readonly PhaseStep[] = [
    { minProgress: 70, phase: 'identifyingVehicle' },
    { minProgress: 20, phase: 'readingVin' },
    { minProgress: 0, phase: 'pairing' },
];

/** How a module stands by the way it answered, before its DTCs are counted. */
const MODULE_STATUS_BY_COMM_STATUS: Readonly<Record<Ssm5CommStatus, Ssm5ModuleStatus>> = {
    ERROR: 'noConnection',
    OK: 'connected',
    UNKNOWN: 'notEquipped',
};

/** The connect error of each endpoint error id. */
const ERROR_CODE_BY_ERROR_ID: Readonly<Partial<Record<number, Ssm5ConnectErrorCode>>> = {
    100: 'unauthorized',
    200: 'invalidId',
    201: 'invalidId',
    202: 'invalidId',
    204: 'invalidId',
    300: 'notPaired',
    301: 'vinReadFailed',
    10000: 'unknown',
};

/** The connect error of each failed status code, for lines without a known error id. */
const ERROR_CODE_BY_STATUS: Readonly<Partial<Record<number, Ssm5ConnectErrorCode>>> = {
    400: 'invalidId',
    403: 'unauthorized',
    404: 'invalidId',
    500: 'unknown',
};

/**
 * Whether reading the DTCs again can recover from a failed read with each error id of the endpoint: it can
 * when the Cloud Connector was taken by another use (101), failed to read the DTCs this time (303) or the
 * system erred (10000), and cannot when the user is not authorized (100), the ID is only spaces (202) or the
 * vehicle information could not be obtained (205).
 */
const IS_RECOVERABLE_BY_DTC_READ_ERROR_ID: Readonly<Partial<Record<number, boolean>>> = {
    100: false,
    101: true,
    202: false,
    205: false,
    303: true,
    10000: true,
};

/** The status codes of a failed DTC read without a known error id that reading again can recover from. */
const RECOVERABLE_DTC_READ_STATUSES: ReadonlySet<number> = new Set([500]);

/** The DTC statuses of a historical DTC; a DTC with any other status is current. */
const HISTORY_DTC_STATUSES: ReadonlySet<string> = new Set(['past']);

/** The freeze frame errors for data that is no longer stored. */
const GONE_FREEZE_FRAME_ERROR_CODES: ReadonlySet<GetFreezeFramesErrorResponse['code']> = new Set<
    GetFreezeFramesErrorResponse['code']
>(['NOT_FOUND']);

/** The contract document format of each domain format. */
const FREEZE_FRAME_DOCUMENT_FORMAT_BY_FORMAT: Readonly<
    Record<Ssm5FreezeFramesDocumentFormat, GetFreezeFramesDocumentRequest['format']>
> = { csv: 'CSV', pdf: 'PDF' };

/** The domain kind of each captured moment. */
const FREEZE_FRAME_KIND_BY_KIND: Readonly<{
    [Kind in GetFreezeFramesFrameResponse['kind']]: Lowercase<Kind>;
}> = {
    DETECT: 'detect',
    HISTORY: 'history',
    POST: 'post',
};

/** The domain state of each captured value. */
const FREEZE_FRAME_STATE_BY_STATE: Readonly<{
    [State in GetFreezeFramesValueResponse['state']]: Lowercase<State>;
}> = {
    EMPTY: 'empty',
    INVALID: 'invalid',
    VALID: 'valid',
};

/** Text that holds decimal digits only, so neither a sign, a fraction, an exponent nor a hex prefix reads as a count. */
const DECIMAL_DIGITS = /^\d+$/;

/**
 * The character codes that mark a description as unreadable: the control characters (below the space, then
 * delete through the C1 block), half of a surrogate pair left on its own by text cut short, and the
 * character a decoder puts in place of bytes it could not read.
 */
const UNREADABLE_CHARACTER_CODE_RANGES: ReadonlyArray<readonly [number, number]> = [
    [0, 31],
    [127, 159],
    [55296, 57343],
    [65533, 65533],
];

/** The control characters a readable description may hold, as white space: tab, line feed and carriage return. */
const WHITE_SPACE_CHARACTER_CODES: ReadonlySet<number> = new Set([9, 10, 13]);

/**
 * Read the connect phase an in-progress line reports.
 *
 * @param line - a streamed lookup line
 * @returns the phase, or undefined when the line is not an in-progress one
 */
export function toConnectPhase(line: GetVehicleByCloudConnectorIdLine): Ssm5ConnectPhase | undefined {
    if (line.state !== 'in-progress') {
        return undefined;
    }

    return PHASE_STEPS.find((step) => line.progress >= step.minProgress)?.phase;
}

/**
 * Turn the last lookup line into the outcome of the connect attempt.
 *
 * @param line - the last streamed lookup line
 * @param cloudConnectorId - the Cloud Connector ID the attempt was made with
 * @returns the session when the vehicle was read, else why the attempt failed
 */
export function toConnectResult(line: GetVehicleByCloudConnectorIdLine, cloudConnectorId: string): Ssm5ConnectResult {
    if (line.state !== 'completed') {
        return { errorCode: toConnectErrorCode(line), success: false };
    }

    return line.vin?.vin
        ? { session: { cloudConnectorId, vin: line.vin.vin }, success: true }
        : { errorCode: 'vinReadFailed', success: false };
}

/**
 * Read the modules of the diagram: a module that did not answer has no connection, one that answered
 * with a DTC the panel lists is connected with errors, one that answered without is connected, and one
 * the backend could not tell about is not equipped, like a cell it does not list at all. The DTCs of a
 * module are read as the DTC panel reads them, grouped by system, and the status follows that list, so
 * a module never reads as one with errors while its panel has no DTC to show.
 *
 * @param ecus - the modules as the backend reported them
 * @returns the modules, in the order they were reported
 */
export function toDiagramModules(ecus: readonly Ssm5EcuRecord[]): Ssm5DiagramModule[] {
    return ecus.map((ecu) => {
        const dtcModules = toDtcModules(ecu.dtcList);

        return {
            cid: toUnitId(ecu.cid),
            cvn: toUnitId(ecu.cvn),
            dtcCount: ecu.dtcCount,
            dtcModules,
            hasPrimaryDtc: ecu.hasPrimaryDtc,
            id: ecu.diagramEcuId,
            isReproAvailable: ecu.reproAvailable,
            name: ecu.displayName,
            reproUrl: toUnitId(ecu.reproUrl),
            status: toModuleStatus(ecu.commStatus, dtcModules),
        };
    });
}

/**
 * Turn the last line of a DTC read into its outcome. A completed read keeps the systems that reported a
 * DTC, drops the records without a code and merges the records that read the same; a read that did not
 * complete tells only whether reading again can recover, never the error it carried.
 *
 * @param line - the last streamed line of the read
 * @returns what the read found, else whether it can be recovered
 */
export function toDtcsResult(line: GetAllDtcsLine): Ssm5DtcsResult {
    if (line.state !== 'completed') {
        return { isRecoverable: isRecoverableDtcRead(line), success: false };
    }

    const block = Array.isArray(line.dtcs) ? undefined : line.dtcs;

    return {
        readout: {
            modules: toDtcModules(block?.data ?? []),
            tripTime: toTripTime(block?.cgw_igon_time, block?.cgw_igon_mill_sec),
        },
        success: true,
    };
}

/**
 * Map the document format to the contract's format.
 *
 * @param format - the requested domain format
 * @returns the contract document format
 */
export function toFreezeFramesDocumentFormat(
    format: Ssm5FreezeFramesDocumentFormat
): GetFreezeFramesDocumentRequest['format'] {
    return FREEZE_FRAME_DOCUMENT_FORMAT_BY_FORMAT[format];
}

/**
 * Turn a freeze frame response into the readout or whether its data is no longer stored.
 *
 * @param response - the response of the read
 * @returns what the read found, else whether reading again cannot bring the data back
 */
export function toFreezeFramesResult(response: GetFreezeFramesResponse): Ssm5FreezeFramesResult {
    if ('code' in response) {
        return { isGone: GONE_FREEZE_FRAME_ERROR_CODES.has(response.code), success: false };
    }

    return { readout: toFreezeFrameReadout(response), success: true };
}

/**
 * Turn a recent-connection record into the domain connection.
 *
 * @param record - the record the lookup answered with
 * @returns the recent connection
 */
export function toRecentConnection(record: RecentConnectionRecord): Ssm5RecentConnection {
    return { cloudConnectorId: record.cloudConnectorId, connectedAt: new Date(record.connectedAt), vin: record.vin };
}

/**
 * Read the supply voltage of the vehicle: the battery reading, when the vehicle reported a valid one.
 *
 * @param reading - the battery reading, if the backend read one
 * @returns the voltage in volts, or undefined without a valid reading
 */
export function toSupplyVoltage(reading: Ssm5BatteryVoltage | undefined): number | undefined {
    return reading?.is_valid && Number.isFinite(reading.voltage) ? reading.voltage : undefined;
}

/**
 * Whether the text holds a character that cannot be shown: a control character other than white space, half
 * of a surrogate pair on its own, or the mark a decoder leaves for bytes it could not read. The text is read
 * character by character, so a whole pair, such as an emoji, reads as the one character it is.
 *
 * @param text - the text
 * @returns true when the text cannot be shown as it is
 */
function hasUnreadableCharacter(text: string): boolean {
    return Array.from(text).some((character) => {
        const code = character.codePointAt(0) ?? 0;

        return (
            !WHITE_SPACE_CHARACTER_CODES.has(code) &&
            UNREADABLE_CHARACTER_CODE_RANGES.some(([first, last]) => code >= first && code <= last)
        );
    });
}

/**
 * Whether reading the DTCs again can recover from a read that did not complete: by its error id, else by
 * its status code when the id is missing or not a known one. A stream that ended before its outcome can
 * always be read again.
 *
 * @param line - the last streamed line of the read
 * @returns true when reading again can succeed within the same session
 */
function isRecoverableDtcRead(line: GetAllDtcsLine): boolean {
    if (line.state !== 'failed') {
        return true;
    }

    const byErrorId =
        typeof line.error_id === 'number' ? IS_RECOVERABLE_BY_DTC_READ_ERROR_ID[line.error_id] : undefined;

    return byErrorId ?? RECOVERABLE_DTC_READ_STATUSES.has(line.code);
}

/**
 * Map a failed line to a connect error: by its error id first, then by its status code.
 *
 * @param line - the failed lookup line
 * @returns the connect error
 */
function toConnectErrorCode(line: GetVehicleByCloudConnectorIdLine): Ssm5ConnectErrorCode {
    const byErrorId = line.error_id === null ? undefined : ERROR_CODE_BY_ERROR_ID[line.error_id];

    return byErrorId ?? ERROR_CODE_BY_STATUS[line.code] ?? 'unknown';
}

/**
 * Read a whole count that is not negative, from a number or its decimal text.
 *
 * @param value - the reported value
 * @returns the count, or undefined when the value is not one
 */
function toCount(value: unknown): number | undefined {
    const count = typeof value === 'string' && DECIMAL_DIGITS.test(value.trim()) ? Number(value) : value;

    return typeof count === 'number' && Number.isInteger(count) && count >= 0 ? count : undefined;
}

/**
 * Turn a DTC record into the domain DTC.
 *
 * @param record - a record with a code
 * @param code - the code of the record
 * @returns the DTC
 */
function toDtc(record: Ssm5DtcRecord, code: string): Ssm5Dtc {
    return {
        code,
        description: toDtcDescription(record.description),
        freezeFrames: toFreezeFrames(record),
        isHistory: HISTORY_DTC_STATUSES.has(record.dtc_status),
        isSecondary: record.is_chain_dtc,
        relatedDocumentCount: toCount(record.related_documents_count),
        setAt: toTripTime(record.igon_time, record.igon_mill_sec),
    };
}

/**
 * Read a DTC description: text that can be shown, none, or a value that cannot be shown as one.
 *
 * @param description - the reported description
 * @returns the description
 */
function toDtcDescription(description: unknown): Ssm5DtcDescription {
    if (description === null || description === undefined) {
        return { state: 'empty' };
    }

    if (typeof description !== 'string' || hasUnreadableCharacter(description)) {
        return { state: 'invalid' };
    }

    const text = description.trim();

    return text === '' ? { state: 'empty' } : { state: 'valid', text };
}

/**
 * Group the records with a code by system, in the order they were reported. Records that read the same
 * in the panel are kept once, whatever else tells them apart, so no DTC of a system shows twice.
 *
 * @param records - the records of every system
 * @returns the systems that reported at least one DTC
 */
function toDtcModules(records: readonly Ssm5DtcRecord[]): Ssm5DtcModule[] {
    const modules = new Map<string, Ssm5DtcModule>();
    const reported = new Set<string>();

    for (const record of records) {
        if (!record.code) {
            continue;
        }

        const dtc = toDtc(record, record.code);
        const shownAs = JSON.stringify([record.system, dtc]);

        if (reported.has(shownAs)) {
            continue;
        }

        reported.add(shownAs);

        const module = modules.get(record.system) ?? { dtcs: [], system: record.system };
        module.dtcs.push(dtc);
        modules.set(record.system, module);
    }

    return Array.from(modules.values());
}

/**
 * Read a captured moment, keeping the contract's sequence for history and post frames.
 *
 * @param frame - the reported moment
 * @returns the captured moment
 */
function toFreezeFrame(frame: GetFreezeFramesFrameResponse): Ssm5FreezeFrame {
    if (frame.kind === 'DETECT') {
        return { kind: FREEZE_FRAME_KIND_BY_KIND[frame.kind] };
    }

    return { kind: FREEZE_FRAME_KIND_BY_KIND[frame.kind], sequence: frame.sequence };
}

/**
 * Read one PID and its values in their reported order.
 *
 * @param parameter - the reported PID
 * @returns the PID and its captured values
 */
function toFreezeFrameParameter(parameter: GetFreezeFramesParameterResponse): Ssm5FreezeFrameParameter {
    return { name: parameter.name, values: parameter.values.map((value) => toFreezeFrameValue(value, parameter.unit)) };
}

/**
 * Read the freeze frame data without changing the order or text the backend reported.
 *
 * @param response - the reported readout
 * @returns the readout
 */
function toFreezeFrameReadout(response: GetFreezeFramesReadoutResponse): Ssm5FreezeFrameReadout {
    return {
        dtcCode: response.dtcCode,
        frames: response.frames.map(toFreezeFrame),
        location: response.location ?? undefined,
        module: response.module,
        parameters: response.parameters.map(toFreezeFrameParameter),
        recordedAt: toRecordedAt(response.recordedAt),
    };
}

/**
 * Read the freeze frame data of a DTC. A DTC without freeze frame data, or with a valid count of zero,
 * has none; a count that cannot be read never reads as none.
 *
 * @param record - the DTC record
 * @returns the freeze frame data
 */
function toFreezeFrames({ ffd_count, has_ffd }: Ssm5DtcRecord): Ssm5FreezeFrames {
    const count = toCount(ffd_count);

    if (has_ffd === false || count === 0) {
        return { state: 'none' };
    }

    return count === undefined ? { state: 'uncounted' } : { count, state: 'counted' };
}

/**
 * Translate a captured value's state, adding the PID's unit to the text of a VALID value when it has one.
 *
 * @param value - the reported value
 * @param unit - the PID's unit, or null when it has none
 * @returns the captured value as shown
 */
function toFreezeFrameValue(value: GetFreezeFramesValueResponse, unit: string | null): Ssm5FreezeFrameValue {
    switch (value.state) {
        case 'EMPTY':
        case 'INVALID':
            return { state: FREEZE_FRAME_STATE_BY_STATE[value.state] };
        case 'VALID':
            return {
                state: FREEZE_FRAME_STATE_BY_STATE[value.state],
                text: unit === null ? value.value : `${value.value} ${unit}`,
            };
    }
}

/**
 * How a module stands: by the way it answered, then by whether the panel has a DTC of it to list.
 *
 * @param commStatus - how the module answered
 * @param dtcModules - the module's DTCs as the panel lists them
 * @returns the module's status
 */
function toModuleStatus(commStatus: Ssm5CommStatus, dtcModules: readonly Ssm5DtcModule[]): Ssm5ModuleStatus {
    const status = MODULE_STATUS_BY_COMM_STATUS[commStatus];

    return status === 'connected' && dtcModules.length > 0 ? 'withErrors' : status;
}

/**
 * Read the time of the event when the reported text is a date.
 *
 * @param recordedAt - the reported date, or null when none was reported
 * @returns the date, or undefined when none can be read
 */
function toRecordedAt(recordedAt: string | null): Date | undefined {
    if (recordedAt === null) {
        return undefined;
    }

    const date = new Date(recordedAt);

    return Number.isNaN(date.getTime()) ? undefined : date;
}

/**
 * Read an ignition cycle and a time into it, from numbers or their decimal text.
 *
 * @param trip - how many times the ignition had been turned on
 * @param elapsedMs - the time into the ignition cycle, in milliseconds
 * @returns both, or undefined when either cannot be read
 */
function toTripTime(trip: unknown, elapsedMs: unknown): Ssm5TripTime | undefined {
    const tripCount = toCount(trip);
    const elapsed = toCount(elapsedMs);

    return tripCount === undefined || elapsed === undefined ? undefined : { elapsedMs: elapsed, trip: tripCount };
}

/**
 * Read a text field of a module, such as its calibration id or its reprogramming link: text with something in it.
 *
 * @param value - the reported value
 * @returns the text, trimmed, or undefined when none was reported
 */
function toUnitId(value: string | null | undefined): string | undefined {
    const id = value?.trim();

    return id ? id : undefined;
}
